// .trisof/project.yml (docs/features/23-project-file.md): one file per repo that
// says what the project is made of, how to build and test it, and where each
// part runs in each environment. Our projects are not uniform (Next on Vercel,
// Hono on Cloudflare, Expo, PM2 on Hetzner, Trigger.dev), so the file describes
// the stack instead of forcing one. Every object is strict: a typo is an error
// on the commit, not a setting silently ignored.
import { z } from 'zod'

const NAME = /^[a-z][a-z0-9-]{0,39}$/
const ENV_VAR = /^[A-Z_][A-Z0-9_]{0,63}$/
const name = (what: string) => z.string().regex(NAME, `${what}: lowercase letters, digits and dashes, starting with a letter`)
const envVar = z.string().regex(ENV_VAR, 'env var names are UPPER_SNAKE_CASE')
/** A path inside the repo: never absolute, never climbing out. */
const repoPath = z.string().min(1).refine((p) => !p.startsWith('/') && !p.split('/').includes('..'), 'must be a path inside the repo')
const cmd = z.string().trim().min(1)
const branch = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._\/-]*$/, 'not a branch name').max(100)

/** The commands a repo or one app answers to. Each runs from the app's path. */
export const ProjectCommands = z.object({
  install: cmd.optional(),
  build: cmd.optional(),
  dev: cmd.optional(),
  start: cmd.optional(),
  lint: cmd.optional(),
  typecheck: cmd.optional(),
  /** Fast tests that need nothing outside the container. Run on every build. */
  unit: cmd.optional(),
  /** Tests that need a database or other services (see tests.integration.needs). */
  integration: cmd.optional(),
  /** Browser tests; related spec paths are appended for a ticket. */
  e2e: cmd.optional(),
  /** Code generation that must be clean before a commit (e.g. DB types). */
  codegen: cmd.optional(),
}).strict()
export type ProjectCommands = z.infer<typeof ProjectCommands>

export const PACKAGE_MANAGERS = ['npm', 'pnpm', 'yarn', 'bun'] as const
export const Runtime = z.object({
  /** Major or exact version, e.g. "22" or "20.18". */
  node: z.string().regex(/^\d+(\.\d+){0,2}$/, 'node: a version like "22" or "20.18"'),
  /** "pnpm", or pinned like "pnpm@10.3.0". */
  packageManager: z.string().regex(/^(npm|pnpm|yarn|bun)(@\d+(\.\d+){0,2})?$/, 'packageManager: npm, pnpm, yarn or bun, optionally @version'),
  /** Extra system packages the container needs (apt names), e.g. chromium deps or JDK. */
  apt: z.array(z.string().regex(/^[a-z0-9][a-z0-9.+-]*$/)).default([]),
  /** Extra tools, e.g. "supabase", "wrangler", "java@17", "android-sdk". */
  tools: z.array(z.string().min(1).max(60)).default([]),
}).strict()

/** Another repo checked out beside this one for builds and tests (speqt-mobile needs clear-cut). */
export const LinkedRepo = z.object({
  repo: z.string().regex(/^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/, 'repo: owner/name'),
  /** Where it goes, relative to this repo's root; may be a sibling ("../clear-cut"). */
  path: z.string().min(1).refine((p) => !p.startsWith('/'), 'path must be relative'),
  /** Default: its default branch. */
  branch: branch.optional(),
  /** What it is for, for people and Claude. */
  why: z.string().max(300).optional(),
}).strict()

// ── hosts ──────────────────────────────────────────────────────────────────
export const APP_KINDS = ['web', 'api', 'worker', 'jobs', 'mobile', 'server', 'site', 'package'] as const
export const HOSTS = ['vercel', 'cloudflare-pages', 'cloudflare-workers', 'hetzner', 'trigger', 'expo', 'none'] as const

/** Per environment: which Vercel target and domain. */
const binding = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'a binding name like DB or ASSETS')
const cfName = z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/, 'lowercase letters, digits and dashes')
/** Five-field cron, e.g. "0 3 * * *". */
const cronExpr = z.string().regex(/^\S+( \S+){4}$/, 'a cron like "0 3 * * *"')
const CronJob = z.object({ path: z.string().startsWith('/'), schedule: cronExpr, description: z.string().max(200).optional() }).strict()
const VercelEnv = z.object({ target: z.string().min(1).max(60).optional(), alias: z.string().min(1).optional(), url: z.string().url().optional() }).strict()
export const VercelHost = z.object({
  project: z.string().min(1).max(100),
  team: z.string().min(1).max(100).optional(),
  /** git: Vercel's GitHub integration deploys; cli: CI runs `vercel deploy`. */
  deployBy: z.enum(['git', 'cli']).default('git'),
  /** Vercel Cron Jobs (as in vercel.json): the route called and when. */
  crons: z.array(CronJob).default([]),
  envs: z.record(z.string(), VercelEnv).default({}),
}).strict()

/** Per environment: the wrangler env section and the resource names it uses. */
const CloudflareEnv = z.object({
  /** `wrangler --env <name>`; omit for the top-level config. */
  wranglerEnv: z.string().min(1).max(60).optional(),
  /** Worker or Pages project name in that environment. */
  name: z.string().min(1).max(100).optional(),
  /** Pages: the branch deployed to (`wrangler pages deploy --branch`). */
  pagesBranch: branch.optional(),
  url: z.string().url().optional(),
  /**
   * The Cloudflare resources this environment uses, binding → resource name
   * (`DB: rhythm-uat`). Setup makes any that are missing; the nightly check
   * says which are gone. Local runs use wrangler's own local copies.
   */
  d1: z.record(binding, cfName).default({}),
  r2: z.record(binding, cfName).default({}),
  kv: z.record(binding, cfName).default({}),
  queues: z.record(binding, cfName).default({}),
  /** Cron triggers here, when they differ from the app's `crons`. */
  crons: z.array(cronExpr).optional(),
}).strict()
export const CloudflareHost = z.object({
  /** wrangler.jsonc / wrangler.toml, relative to the app. */
  config: repoPath.optional(),
  account: z.string().min(1).max(100).optional(),
  /** Bindings a test or dev run must fake or provide, by kind. Documentation for Claude. */
  bindings: z.object({
    d1: z.array(z.string()).default([]),
    r2: z.array(z.string()).default([]),
    kv: z.array(z.string()).default([]),
    queues: z.array(z.string()).default([]),
    durableObjects: z.array(z.string()).default([]),
    workflows: z.array(z.string()).default([]),
    services: z.array(z.string()).default([]),
    other: z.array(z.string()).default([]),
  }).strict().optional(),
  crons: z.array(z.string()).default([]),
  envs: z.record(z.string(), CloudflareEnv).default({}),
}).strict()

const HetznerEnv = z.object({
  /** Server names as in Hetzner / our inventory. */
  servers: z.array(z.string().min(1).max(100)).min(1),
  url: z.string().url().optional(),
}).strict()
export const HetznerHost = z.object({
  /** How the process is kept running. */
  process: z.enum(['pm2', 'systemd', 'docker', 'docker-compose']),
  /** PM2 ecosystem file, unit name, compose file…, relative to the app. */
  config: z.string().min(1).optional(),
  /** Command CI runs to deploy (gets the env name as $TRISOF_ENV). */
  deploy: cmd.optional(),
  port: z.number().int().min(1).max(65535).optional(),
  /** Reverse proxy in front, if any. */
  proxy: z.enum(['caddy', 'nginx', 'traefik', 'none']).default('none'),
  envs: z.record(z.string(), HetznerEnv).default({}),
}).strict()

const TriggerEnv = z.object({
  /** Trigger.dev project ref, when each environment is its own project (Rhythm). */
  project: z.string().regex(/^proj_[a-z0-9]+$/).optional(),
  /** Trigger.dev environment: dev, staging or prod. */
  env: z.enum(['dev', 'staging', 'prod']).optional(),
  /** Deploys need a person (Rhythm prod). */
  manual: z.boolean().default(false),
}).strict()
export const TriggerHost = z.object({
  /** Default project ref; an environment may override it. */
  project: z.string().regex(/^proj_[a-z0-9]+$/).optional(),
  config: repoPath.default('trigger.config.ts'),
  /** Folder of task files. */
  tasks: repoPath.optional(),
  runtime: z.string().max(30).optional(),
  /** Scheduled tasks: task id and cron (declared in code with schedules.task). */
  schedules: z.array(z.object({ task: z.string().min(1).max(100), cron: cronExpr, description: z.string().max(200).optional() }).strict()).default([]),
  envs: z.record(z.string(), TriggerEnv).default({}),
}).strict()

const MobileEnv = z.object({
  /** EAS build profile (eas.json), or the local build variant (e.g. APP_VARIANT). */
  profile: z.string().min(1).max(60).optional(),
  /** EAS Update channel. */
  channel: z.string().min(1).max(60).optional(),
  /** Android application id / iOS bundle id in this environment. */
  androidPackage: z.string().regex(/^[a-zA-Z][\w]*(\.[a-zA-Z][\w]*)+$/).optional(),
  iosBundleId: z.string().regex(/^[a-zA-Z][\w-]*(\.[a-zA-Z][\w-]*)+$/).optional(),
  /** How builds reach people in this environment. */
  distribution: z.enum(['store', 'internal', 'testflight', 'apk', 'none']).optional(),
  /** Command that produces the build here (local builders). */
  build: cmd.optional(),
}).strict()
export const MobileHost = z.object({
  framework: z.enum(['expo', 'react-native']).default('expo'),
  /** Managed: no ios/ android/ folders, prebuild makes them. */
  workflow: z.enum(['managed', 'bare']).default('managed'),
  platforms: z.array(z.enum(['android', 'ios', 'web'])).min(1),
  /** eas: EAS Build; local: a script on a machine (needs JDK/Android SDK or macOS). */
  builder: z.enum(['eas', 'local']),
  /** Over-the-air updates. */
  updates: z.enum(['eas-update', 'self', 'none']).default('none'),
  expoProject: z.string().min(1).max(100).optional(),
  /** What runs on Linux without a device: typecheck/unit/web export. */
  linuxChecks: z.array(cmd).default([]),
  envs: z.record(z.string(), MobileEnv).default({}),
}).strict()

/** One deployable or buildable part of the product. */
export const ProjectApp = z.object({
  kind: z.enum(APP_KINDS),
  /** Folder, relative to the repo (or a linked repo's path). "." for the root. */
  path: z.string().min(1).default('.'),
  /** Free text: next, vite, hono, express, expo, astro, remix… */
  framework: z.string().min(1).max(40).optional(),
  host: z.enum(HOSTS),
  description: z.string().max(300).optional(),
  commands: ProjectCommands.default({}),
  /** Dev server for previews and browser proof. */
  dev: z.object({
    /** Where it would like to listen; the platform gives the first free port from here up in PORT. */
    port: z.number().int().min(1).max(65535),
    path: z.string().startsWith('/').default('/'),
    /**
     * Env set for this app's dev server, where `${port.<app>}` is the port that
     * app got this time (`VITE_BACKEND_ORIGIN: http://127.0.0.1:${port.server}/api`).
     * Ports move when one is taken, so an app finds another this way, not by a fixed number.
     */
    env: z.record(z.string().regex(/^[A-Z_][A-Z0-9_]*$/, 'env var names are UPPER_SNAKE_CASE'), z.string().max(500)).optional(),
    /**
     * Test users of the local stack, picked in the Preview's "Sign in as".
     * Their passwords reach the app only through `${dev.users}` in `env` (a
     * JSON email → password map); the dashboard is given emails and labels.
     */
    users: z.array(z.object({ email: z.string().email(), password: z.string().min(1).max(200), label: z.string().max(40).optional() }).strict()).max(20).optional(),
  }).strict().optional(),
  /** Env var names this app reads (values never go in this file). */
  env: z.array(envVar).default([]),
  /**
   * The .env file this app reads in Claude's container, relative to the app's
   * `path` (e.g. `.env.local`). The platform writes it with the local values
   * of the app's secrets; it lives in memory and is linked into every
   * checkout, ignored by git. Several apps may name the same file.
   */
  envFile: repoPath.refine((p) => /^[\w./-]+$/.test(p), 'letters, digits, . _ - and /').optional(),
  vercel: VercelHost.optional(),
  cloudflare: CloudflareHost.optional(),
  hetzner: HetznerHost.optional(),
  trigger: TriggerHost.optional(),
  mobile: MobileHost.optional(),
}).strict().superRefine((a, ctx) => {
  // The host's own block must be there, and only that one: a Vercel app with a
  // cloudflare: block is a copy-paste mistake, not a second host.
  const block: Record<string, string | null> = {
    vercel: 'vercel', 'cloudflare-pages': 'cloudflare', 'cloudflare-workers': 'cloudflare', hetzner: 'hetzner', trigger: 'trigger', expo: 'mobile', none: null,
  }
  const want = block[a.host]
  for (const k of ['vercel', 'cloudflare', 'hetzner', 'trigger', 'mobile'] as const) {
    if (a[k] && k !== want) ctx.addIssue({ code: 'custom', path: [k], message: `host is ${a.host}, so ${k}: does not belong here` })
  }
  if (want && !a[want as 'vercel']) ctx.addIssue({ code: 'custom', path: [want], message: `host ${a.host} needs a ${want}: block` })
  if (a.kind === 'mobile' && a.host !== 'expo' && a.host !== 'none') ctx.addIssue({ code: 'custom', path: ['host'], message: 'a mobile app is hosted on expo (or none)' })
})
export type ProjectApp = z.infer<typeof ProjectApp>

// ── database ───────────────────────────────────────────────────────────────
export const ProjectDatabase = z.object({
  provider: z.enum(['supabase', 'postgres', 'd1', 'none']),
  /** Postgres schemas the app uses besides public. */
  schemas: z.array(z.string().regex(/^[a-z_][a-z0-9_]*$/)).default(['public']),
  migrations: z.object({
    dir: repoPath,
    /** supabase-cli: `supabase db push`; script: the repo's own runner; manual: a person. */
    tool: z.enum(['supabase-cli', 'script', 'wrangler-d1', 'manual']),
    /** Applies pending migrations to $TRISOF_ENV's database. */
    apply: cmd.optional(),
    /** Shows what apply would do, changing nothing. */
    dryRun: cmd.optional(),
    /** Folder of migrations held back on purpose; never applied by the robot. */
    held: repoPath.optional(),
  }).strict().optional(),
  /** A database inside the container for tests. */
  local: z.object({
    supported: z.boolean(),
    start: cmd.optional(),
    reset: cmd.optional(),
    /** Docker is needed (supabase start). */
    docker: z.boolean().default(false),
  }).strict().optional(),
  seed: cmd.optional(),
  /** Generated DB types that must match the schema. */
  types: z.object({ command: cmd, file: repoPath }).strict().optional(),
}).strict()

// ── supabase ───────────────────────────────────────────────────────────────
// What the project uses of Supabase, the same in every environment. uat and
// prod are Supabase projects (environments.*.supabase.ref); local is the
// Supabase stack in the container (`supabase start`), never a cloud dev project.
export const AUTH_PROVIDERS = ['email', 'magic-link', 'phone', 'google', 'apple', 'github', 'azure', 'facebook', 'twitter', 'discord', 'slack', 'linkedin', 'saml'] as const
export const ProjectSupabase = z.object({
  /** supabase/config.toml; the local stack and `supabase config push` read it. */
  config: repoPath.default('supabase/config.toml'),
  auth: z.object({
    providers: z.array(z.enum(AUTH_PROVIDERS)).default(['email']),
    /** Paths added to each environment's url for redirects, e.g. /auth/callback. */
    redirectPaths: z.array(z.string().startsWith('/')).default([]),
    /** Supabase sends mail through this SMTP provider in uat/prod; local uses Inbucket. */
    smtp: z.enum(['supabase', 'resend', 'sendgrid', 'postmark', 'ses', 'other']).default('supabase'),
  }).strict().optional(),
  storage: z.object({
    buckets: z.array(z.object({ name: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,62}$/), public: z.boolean().default(false), description: z.string().max(200).optional() }).strict()).default([]),
  }).strict().optional(),
  functions: z.object({
    dir: repoPath.default('supabase/functions'),
    /** Edge functions deployed to uat and prod and served locally. */
    names: z.array(z.string().regex(/^[a-z0-9][a-z0-9_-]{0,62}$/)).min(1),
    /** Names called without a user JWT (webhooks). */
    noJwt: z.array(z.string()).default([]),
  }).strict().optional(),
  realtime: z.boolean().default(false),
  /** pg_cron jobs: name, schedule, SQL or an edge function name. */
  cron: z.array(z.object({ name: z.string().min(1).max(100), schedule: cronExpr, run: z.string().min(1).max(500) }).strict()).default([]),
}).strict()

// ── local ──────────────────────────────────────────────────────────────────
// dev is not a cloud environment: it is what runs inside the container.
export const ProjectLocal = z.object({
  /** Integration branch ticket PRs may go to before uat; never deployed. */
  branch: branch.optional(),
  /** Run the Supabase stack in the container (db, auth, storage, functions per config.toml). */
  supabase: z.boolean().default(false),
  /** Start everything a local run needs; default `supabase start` when supabase is on. */
  start: cmd.optional(),
  /**
   * After every start, with the stack's values filled in: bring the database up
   * to date and seed it, e.g. a project's own migration runner. Must be safe to
   * run again; it never wipes anything.
   */
  setup: cmd.optional(),
  /** Back to a clean state: migrations and seed again. Default `supabase db reset --local`. */
  reset: cmd.optional(),
  /** Non-secret values for local runs, e.g. NEXT_PUBLIC_SUPABASE_URL: http://127.0.0.1:54321. */
  env: z.record(envVar, z.string().max(500)).default({}),
}).strict()
export type ProjectLocal = z.infer<typeof ProjectLocal>

// ── environments ───────────────────────────────────────────────────────────
/** The only cloud environments. dev is local, in the container (`local:`). */
export const CLOUD_ENVS = ['uat', 'prod'] as const
export const ProjectEnvironment = z.object({
  /** The git branch that is this environment. */
  branch,
  /** auto: a merge deploys; manual: a person starts it. */
  deploy: z.enum(['auto', 'manual']).default('auto'),
  /** The app's main address here. */
  url: z.string().url().optional(),
  supabase: z.object({
    /** Project ref, e.g. abcdefghijklmnop. */
    ref: z.string().regex(/^[a-z0-9]{6,40}$/),
    url: z.string().url().optional(),
  }).strict().optional(),
  /**
   * Real people's data. The robot never points a run at a protected
   * environment's database or services (the prodSupabaseRef guard).
   */
  protected: z.boolean().default(false),
}).strict()
export type ProjectEnvironment = z.infer<typeof ProjectEnvironment>

// ── secrets ────────────────────────────────────────────────────────────────
export const ProjectSecret = z.object({
  name: envVar,
  description: z.string().max(200).optional(),
  /** Environments it exists in (uat, prod, and local for the container); default all. */
  envs: z.array(z.string()).optional(),
  /** Apps that read it; default all. */
  apps: z.array(z.string()).optional(),
  /** Public (shipped to the browser/app), e.g. NEXT_PUBLIC_*, VITE_*, EXPO_PUBLIC_*. */
  public: z.boolean().default(false),
  /** Claude's containers have it, for local runs. Never one that is prod-only. */
  agent: z.boolean().default(false),
  /** Needed at build time (not only at run time). */
  build: z.boolean().default(false),
  /** A deploy or check fails while it has no value. false: optional. */
  required: z.boolean().default(true),
  /**
   * Where the platform puts the value besides CI jobs that name it: the app's
   * host (Vercel env, Worker secret, server env file, EAS secret), GitHub
   * Actions, Claude's containers. Default: the hosts of its apps.
   */
  deliver: z.array(z.enum(['host', 'github-actions', 'agent', 'ci'])).optional(),
}).strict()

// ── integrations ───────────────────────────────────────────────────────────
// Which connected account (Settings → Connections, by its label) each part of
// the project uses. The account's token stays on the platform; the file names
// it, so the same file recreates the project wherever those labels exist.
const account = z.string().min(1).max(100)
const Account = z.object({ account }).strict()
export const ProjectIntegrations = z.object({
  github: z.object({
    /**
     * app: the One Deployment GitHub App; token: a connected account's token.
     * Left out keeps what the project has: a default of app once flipped a
     * token-mode project (Rhythm) to an App that is not installed on its org.
     */
    auth: z.enum(['app', 'token']).optional(),
    account: account.optional(),
    /** The repo; default the one this file is in. */
    repo: z.string().regex(/^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/, 'repo: owner/name').optional(),
    /** Recreating: make the repo from this template when it does not exist. */
    template: z.string().regex(/^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/).optional(),
    private: z.boolean().default(true),
  }).strict().default({}),
  linear: z.object({
    team: z.string().min(1).max(100),
    /** Linear project the tickets belong to; default all of the team's. */
    project: z.string().min(1).max(100).optional(),
    /** Workflow state names. trigger: moving a ticket here starts a build. */
    states: z.object({
      trigger: z.string().min(1).max(100).optional(),
      build: z.string().min(1).max(100).optional(),
      pr: z.string().min(1).max(100).optional(),
      merged: z.string().min(1).max(100).optional(),
    }).strict().default({}),
    /** Linear user ids: who leads, and the agent user tickets are delegated to. */
    lead: z.string().min(1).max(100).optional(),
    agent: z.string().min(1).max(100).optional(),
  }).strict().optional(),
  slack: z.object({ account: account.optional(), channel: z.string().regex(/^[CG][A-Z0-9]{6,20}$/, 'a channel id like C0123ABCD') }).strict().optional(),
  vercel: Account.optional(),
  cloudflare: Account.optional(),
  supabase: Account.optional(),
  trigger: Account.optional(),
  expo: Account.optional(),
  firebase: Account.optional(),
  'google-play': Account.optional(),
  'app-store': Account.optional(),
}).strict()
export type ProjectIntegrations = z.infer<typeof ProjectIntegrations>

// ── how tickets move ───────────────────────────────────────────────────────
export const TicketingSwitches = z.object({
  /** The lead's Accept on a triage suggestion, and the agent's own status moves, write to Linear. On unless set to false; off: nothing changes a ticket. */
  linearWrites: z.boolean().optional(),
  /** A ticket in Todo owned by the agent's Linear account starts by itself. */
  startTodo: z.boolean().optional(),
  /** A developer's points in a one-week cycle (default 100); the cycle's capacity is this times the project's leads and devs. */
  pointsPerPerson: z.number().int().min(1).max(1000).optional(),
}).strict()
export type TicketingSwitches = z.infer<typeof TicketingSwitches>
// ── how sessions talk to the dashboard ─────────────────────────────────────
export const ChatSwitches = z.object({
  /** Claude runs in stream-json mode, so the chat shows everything it prints and answers its permission prompts (features/26). On unless set to false. */
  stream: z.boolean().optional(),
}).strict()
export type ChatSwitches = z.infer<typeof ChatSwitches>
export const ProjectWorkflow = z.object({
  stage: z.enum(['build', 'support']).optional(),
  /** New tickets are triaged by Claude. */
  autoTriage: z.boolean().optional(),
  /** Start a ticket by itself when triage finds it matches the rule. */
  autoStart: z.string().trim().min(1).max(1000).optional(),
  requireScope: z.boolean().optional(),
  /** The robot merges an easy ticket's PR once every check is green. */
  autopilot: z.boolean().optional(),
  /** The hardest ticket whose PR merges itself: easy (default) or medium. */
  autoMergeUpTo: z.enum(['easy', 'medium']).optional(),
  maxOpenTickets: z.number().int().min(1).max(100).optional(),
  ticketFlow: z.boolean().optional(),
  resumeAfterMin: z.number().int().min(1).max(240).optional(),
  /** The house ticket flow (docs/ticketing.md). Each part is off until set. */
  ticketing: TicketingSwitches.optional(),
  /** The dashboard's chat (features/26). Off until set. */
  chat: ChatSwitches.optional(),
}).strict()
export type ProjectWorkflow = z.infer<typeof ProjectWorkflow>

// ── the file ───────────────────────────────────────────────────────────────
export const PROJECT_FILE = '.trisof/project.yml'

export const ProjectFile = z.object({
  version: z.literal(1),
  /** The One Deployment project key this repo belongs to. */
  project: name('project'),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  runtime: Runtime,
  repos: z.array(LinkedRepo).default([]),
  /** Defaults for the whole repo; an app's own commands win for that app. */
  commands: ProjectCommands.default({}),
  apps: z.record(name('app name'), ProjectApp).refine((a) => Object.keys(a).length > 0, 'at least one app')
    .refine((a) => Object.values(a).every((x) => Object.values(x.dev?.env ?? {}).every((v) => [...v.matchAll(/\$\{port\.([^}]*)\}/g)].every((m) => a[m[1]!]?.dev))),
      'dev.env: ${port.<app>} must name an app with a dev port'),
  database: ProjectDatabase.default({ provider: 'none' }),
  supabase: ProjectSupabase.optional(),
  local: ProjectLocal.default({}),
  environments: z.record(name('environment name'), ProjectEnvironment),
  flow: z.object({
    /** Where ticket PRs go. */
    base: branch,
    /** Environments in promotion order; a person merges each step. */
    promote: z.array(z.string()).min(1),
    /**
     * One "Push to <env>" button per environment listed (in promote order):
     * the branch before it (base, then each promote step) goes in through a
     * pull request that merges once its checks are green, so branch rules hold
     * and the env's own pipeline deploys from the push. Speqt: [uat, prod].
     */
    push: z.array(z.string()).optional(),
    /**
     * Ship to prod from the dashboard (features/24). checks run in the
     * project's container on the base branch's head before shipping; a check
     * that passed on that commit is not run again. workflow: a GitHub Actions
     * workflow file (workflow_dispatch) that promotes base to prod; without
     * one, base is merged into the prod branch.
     */
    ship: z.object({
      checks: z.array(z.object({
        name: z.string().regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters, digits and -').max(40),
        run: cmd.max(2000),
      }).strict()).max(10).default([]),
      workflow: z.string().regex(/^[\w.-]+\.ya?ml$/, 'a workflow file name like dev-to-prod.yml').optional(),
      /**
       * Ship on our own CI instead of GitHub Actions: a schedule in
       * .trisof/ci.yml (usually `ship: manual`), run on the base head. Wins
       * over workflow. With session, Claude tags the commit it checked
       * (refs/tags/trisof-ship-<release>) and the run starts on that commit.
       */
      ci: z.string().regex(/^[a-z0-9_-]{1,40}$/, 'a schedule name from .trisof/ci.yml, like ship').optional(),
      /**
       * Push to prod runs this command (the project's own, e.g. /push-main) in
       * a hidden Claude session on a fresh checkout of the base, instead of the
       * dashboard merging and checking by itself. The dashboard then follows
       * flow.ship.workflow and does flow.ship.linear.
       */
      session: z.string().regex(/^\/[\w:-]{1,60}$/, 'a slash command like /push-main').optional(),
      /** After a green ship: tickets named in the shipped commits move from one Linear state to another (and to a person). */
      linear: z.object({
        from: z.string().min(1).max(100),
        to: z.string().min(1).max(100),
        assignee: z.string().min(1).max(100).optional(),
      }).strict().optional(),
      /** After a green ship: what shipped is posted to the project's Slack channel. */
      announce: z.boolean().optional(),
    }).strict().optional(),
  }).strict(),
  tests: z.object({
    /** What integration tests need. */
    integration: z.object({ needs: z.array(z.enum(['docker', 'local-db', 'network', 'secrets'])).default([]) }).strict().optional(),
    e2e: z.object({
      /** fixtures: no backend; local: app + the local stack; uat: the deployed uat. */
      against: z.enum(['fixtures', 'local', 'uat']),
      browsers: z.array(z.enum(['chromium', 'firefox', 'webkit'])).default(['chromium']),
      /** Env var names the tests read. */
      env: z.array(envVar).default([]),
    }).strict().optional(),
    /** Environment for every test run, e.g. VITEST_MAX_WORKERS: "3". */
    env: z.record(envVar, z.string().max(200)).default({}),
    /** Peak memory of the heaviest step, MB (e.g. a 6 GB typecheck). */
    memoryMb: z.number().int().min(512).max(65536).optional(),
  }).strict().default({}),
  /**
   * CI/CD (features/21). runner trisof: `on:` and `jobs:` here, in the
   * .trisof/ci.yml format, run on our CI servers. github-actions: the repo's
   * .github/workflows run on our own runner on ci-1 (`runs-on: [self-hosted, trisof]`).
   * none: no CI. Left out: .trisof/ci.yml is read as before.
   */
  ci: z.object({
    runner: z.enum(['trisof', 'github-actions', 'none']).default('trisof'),
    /** CI jobs of this project running at once; default 2. */
    parallel: z.number().int().min(1).max(20).optional(),
    on: z.unknown().optional(),
    jobs: z.record(z.string(), z.unknown()).optional(),
  }).strict().optional(),
  secrets: z.array(ProjectSecret).default([]),
  /**
   * push: values typed in Settings → Secrets are written to each app's host
   * (Vercel, Workers, Trigger.dev) and GitHub. check: nothing is written;
   * the hosts are only read and differences shown. For a project whose deploys
   * already work with values set by hand (Rhythm), start with check.
   */
  secretsSync: z.enum(['push', 'check']).default('push'),
  /** How One Deployment works on this repo. */
  agent: z.object({
    /** Container image (images/<name> or a registry ref). */
    image: z.string().min(1).max(200).optional(),
    memoryMb: z.number().int().min(512).max(131072).optional(),
    cpus: z.number().int().min(1).max(32).optional(),
    /** Ticket branches are `<prefix><ticket>-<slug>`. */
    branchPrefix: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._\/-]*$/).max(40).optional(),
    /** Parts of the app tickets are filed under. */
    areas: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
    /** Paths Claude must not change without a person (globs). */
    protectedPaths: z.array(z.string().min(1)).default([]),
    /** Files Claude reads first (CLAUDE.md, docs/…). */
    docs: z.array(repoPath).default([]),
    models: z.object({ triage: z.string().min(1).max(100).optional(), implement: z.string().min(1).max(100).optional() }).strict().default({}),
    maxContainers: z.number().int().min(1).max(64).optional(),
    sessionsPerContainer: z.number().int().min(1).max(32).optional(),
    /** Ticket runs at once across every server. */
    parallel: z.number().int().min(1).max(32).optional(),
    /** Slack conversations with Claude at once, per server. */
    chats: z.number().int().min(1).max(16).optional(),
    /** Git author of the robot's commits. */
    author: z.object({ name: z.string().min(1).max(100), email: z.string().email() }).strict().optional(),
  }).strict().default({}),
  integrations: ProjectIntegrations.default({}),
  workflow: ProjectWorkflow.default({}),
}).strict().superRefine((f, ctx) => {
  const envs = Object.keys(f.environments)
  const apps = Object.keys(f.apps)
  if (!envs.includes('prod')) ctx.addIssue({ code: 'custom', path: ['environments'], message: 'environments needs prod' })
  for (const n of envs) if (!(CLOUD_ENVS as readonly string[]).includes(n)) ctx.addIssue({ code: 'custom', path: ['environments', n], message: n === 'dev' ? 'dev is not a cloud environment: it runs in the container (local:)' : `environments are uat and prod only, not "${n}"` })
  if (f.supabase && f.database.provider !== 'supabase') ctx.addIssue({ code: 'custom', path: ['supabase'], message: 'supabase: needs database.provider supabase' })
  if (f.local.supabase && f.database.provider !== 'supabase') ctx.addIssue({ code: 'custom', path: ['local', 'supabase'], message: 'local.supabase needs database.provider supabase' })
  for (const n of f.supabase?.functions?.noJwt ?? []) if (!f.supabase!.functions!.names.includes(n)) ctx.addIssue({ code: 'custom', path: ['supabase', 'functions', 'noJwt'], message: `unknown function "${n}"` })
  if (f.environments.prod && !f.environments.prod.protected) ctx.addIssue({ code: 'custom', path: ['environments', 'prod', 'protected'], message: 'prod must be protected: true' })
  // Two environments on one Supabase project would make the prod guard useless.
  const refs = new Map<string, string>()
  for (const [n, e] of Object.entries(f.environments)) {
    if (!e.supabase) continue
    const other = refs.get(e.supabase.ref)
    if (other) ctx.addIssue({ code: 'custom', path: ['environments', n, 'supabase', 'ref'], message: `same Supabase project as ${other}` })
    refs.set(e.supabase.ref, n)
  }
  f.flow.promote.forEach((e, i) => { if (!envs.includes(e)) ctx.addIssue({ code: 'custom', path: ['flow', 'promote', i], message: `unknown environment "${e}"` }) })
  f.flow.push?.forEach((e, i) => { if (!f.flow.promote.includes(e)) ctx.addIssue({ code: 'custom', path: ['flow', 'push', i], message: `"${e}" is not a promote step` }) })
  if (f.flow.base !== f.local.branch && !Object.values(f.environments).some((e) => e.branch === f.flow.base)) ctx.addIssue({ code: 'custom', path: ['flow', 'base'], message: `flow.base "${f.flow.base}" is neither local.branch nor an environment's branch` })
  // Per-environment host settings must name environments that exist.
  for (const [an, a] of Object.entries(f.apps)) {
    for (const k of ['vercel', 'cloudflare', 'hetzner', 'trigger', 'mobile'] as const) {
      // A mobile app's local entry is its dev-client build, run against the local stack.
      for (const en of Object.keys(a[k]?.envs ?? {})) if (!envs.includes(en) && !(k === 'mobile' && en === 'local')) ctx.addIssue({ code: 'custom', path: ['apps', an, k, 'envs', en], message: `unknown environment "${en}"` })
    }
  }
  const seen = new Set<string>()
  f.secrets.forEach((s, i) => {
    if (seen.has(s.name)) ctx.addIssue({ code: 'custom', path: ['secrets', i, 'name'], message: `${s.name} is listed twice` })
    seen.add(s.name)
    for (const en of s.envs ?? []) if (en !== 'local' && !envs.includes(en)) ctx.addIssue({ code: 'custom', path: ['secrets', i, 'envs'], message: `${s.name}: unknown environment "${en}"` })
    for (const an of s.apps ?? []) if (!apps.includes(an)) ctx.addIssue({ code: 'custom', path: ['secrets', i, 'apps'], message: `${s.name}: unknown app "${an}"` })
    if (s.agent && s.envs && s.envs.every((en) => f.environments[en]?.protected)) ctx.addIssue({ code: 'custom', path: ['secrets', i, 'agent'], message: `${s.name}: agent: true but it is prod-only` })
  })
})
export type ProjectFile = z.infer<typeof ProjectFile>
